<template>
  <section class="ni-section ni-int" id="int-communication">
    <div class="ni-container">
      <SectionHeader
        variant="dark"
        :subtitle="t('newIndex.sections.intComm.subtitle')"
        more-href="/international"
      >
        <template #title>
          <span class="ni-section-header__title-mark">
            {{ t('newIndex.sections.intComm.title') }}
          </span>
        </template>
      </SectionHeader>

      <div v-if="cates.length" class="ni-int__toolbar">
        <div class="ni-int__toolbar-left">
          <div class="ni-int__cats">
            <div class="ni-int__cat-accent" aria-hidden="true">›</div>
            <div class="ni-int__cat-switch">
              <template v-for="(cate, idx) in cates" :key="cate.id">
                <span v-if="idx > 0" class="ni-int__cat-sep">/</span>
                <button
                  :id="`int-cate-btn-${cate.id}`"
                  type="button"
                  class="ni-int__cat-btn"
                  :class="{ 'ni-int__cat-btn--active': activeCateId === cate.id }"
                  @click="switchCate(cate.id)"
                >
                  {{ cate.title }}
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>

      <div v-if="items.length" class="ni-int__grid">
        <div
          class="ni-int__video ni-int__video--clickable"
          role="button"
          tabindex="0"
          @click="openItemDetail(previewItem)"
          @keydown.enter="openItemDetail(previewItem)"
        >
          <div
            class="ni-int__video-bg"
            :class="{ 'ni-int__video-bg--portrait': isPortrait }"
          >
            <div v-if="isPortrait" class="ni-int__video-blur" aria-hidden="true">
              <img :src="previewItem?.imageUrl" alt="" />
            </div>
            <Transition name="ni-int-fade" mode="out-in">
              <video
                v-if="previewItem?.isVideo && previewItem?.video && isPlaying"
                :key="`video-${previewItem.id}`"
                ref="previewVideoRef"
                class="ni-int__video-el"
                :class="{ 'ni-int__video-el--portrait': isPortrait }"
                :poster="previewItem.imageUrl"
                :src="previewItem.video"
                playsinline
                controlslist="nodownload"
                oncontextmenu="return false"
                @loadedmetadata="onVideoReady"
              />
              <img
                v-else
                :key="previewItem?.id"
                class="ni-int__video-poster"
                :class="{ 'ni-int__video-poster--portrait': isPortrait }"
                :src="previewItem?.imageUrl"
                :alt="previewItem?.title"
                @load="handlePosterLoad"
              />
            </Transition>
            <div
              class="ni-int__video-vignette"
              :class="{ 'ni-int__video-vignette--portrait': isPortrait }"
            ></div>
          </div>

          <div v-if="previewItem?.isVideo" class="ni-int__controls">
            <button
              id="int-play-button-controller"
              type="button"
              @click.stop="togglePlay"
            >
              <span v-if="isPlaying" aria-hidden="true">❚❚</span>
              <span v-else aria-hidden="true">▶</span>
            </button>
            <span>
              {{ isPlaying ? `0:${elapsedSeconds}` : '0:00' }}
            </span>
            <div class="ni-int__progress">
              <div
                class="ni-int__progress-bar"
                :style="{ width: `${videoProgress}%` }"
              ></div>
            </div>
            <span class="ni-int__duration">{{ previewDuration }}</span>
          </div>
        </div>

        <div class="ni-int__cards">
          <article
            v-for="(item, index) in displayItems"
            :id="`int-right-list-card-${item.id}`"
            :key="item.id"
            class="ni-int__card"
            @click="handleCardClick(index)"
          >
            <div class="ni-int__card-thumb">
              <img :src="item.imageUrl" :alt="item.title" />
            </div>
            <div class="ni-int__card-body">
              <h4 class="ni-int__card-title">{{ item.title }}</h4>
              <p v-if="item.summary" class="ni-int__card-summary" v-html="item.summary"></p>
            </div>
          </article>
        </div>
      </div>

      <p v-else class="ni-int__empty">{{ t('newIndex.common.viewMore') }}</p>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { getImageSize } from '@/utils/utils';
import { useExclusiveVideoPlayback } from '@/composables/useExclusiveVideoPlayback.js';
import SectionHeader from './shared/SectionHeader.vue';

const props = defineProps({
  cates: {
    type: Array,
    default: () => [],
  },
  items: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();
const router = useRouter();

const activeCateId = ref(null);
const activeIdx = ref(0);
const isPlaying = ref(false);
const isPortrait = ref(false);
const videoProgress = ref(0);
const previewVideoRef = ref(null);
let progressTimer = null;

const { claimPlayback } = useExclusiveVideoPlayback('int-communicator', {
  onForcePause: () => resetPlayback(),
  getVideoEl: () => previewVideoRef.value,
});

function setPortraitFromDimensions(width, height) {
  if (!width || !height) return;
  isPortrait.value = Number(width) < Number(height);
}

async function detectPortrait(item) {
  if (!item) {
    isPortrait.value = false;
    return;
  }
  if (item.isPortrait || Number(item.direction) === 3) {
    isPortrait.value = true;
    return;
  }
  if (Number(item.direction) === 1 || Number(item.direction) === 2) {
    isPortrait.value = false;
    return;
  }
  const url = item.imageUrl || item.thumb;
  if (!url) {
    isPortrait.value = false;
    return;
  }
  try {
    const size = await getImageSize(url);
    setPortraitFromDimensions(size.width, size.height);
  } catch {
    isPortrait.value = false;
  }
}

function handleVideoMetadata(event) {
  const video = event.target;
  if (video?.videoWidth && video?.videoHeight) {
    setPortraitFromDimensions(video.videoWidth, video.videoHeight);
  }
}

function onVideoReady(event) {
  handleVideoMetadata(event);
  const video = event.target;
  if (!video || !isPlaying.value) return;
  claimPlayback(video);
  video.muted = false;
  video.play().catch(() => {});
}

function pausePreviewVideo() {
  const video = previewVideoRef.value;
  if (video) {
    video.pause();
  }
}

function handlePosterLoad(event) {
  const img = event.target;
  if (img?.naturalWidth && img?.naturalHeight) {
    setPortraitFromDimensions(img.naturalWidth, img.naturalHeight);
  }
}

function itemBelongsToCate(item, cateId) {
  if (cateId == null) return true;
  if (item.cateId != null) {
    return Number(item.cateId) === Number(cateId);
  }
  // 后端未返回 cate 时，在各系列下均展示，避免列表为空
  return true;
}

const currentItems = computed(() => {
  if (!props.items.length) return [];
  if (activeCateId.value == null) return props.items;
  const matched = props.items.filter((item) =>
    itemBelongsToCate(item, activeCateId.value)
  );
  return matched.length ? matched : props.items;
});

const displayItems = computed(() => currentItems.value.slice(0, 3));

const previewItem = computed(
  () => displayItems.value[activeIdx.value] || displayItems.value[0] || null
);

const previewDuration = computed(() => {
  if (!previewItem.value?.isVideo) return '';
  return previewItem.value.videoDuration || '02:30';
});

const elapsedSeconds = computed(() =>
  Math.floor((videoProgress.value / 100) * 15)
    .toString()
    .padStart(2, '0')
);

function stopProgressTimer() {
  if (progressTimer) {
    clearInterval(progressTimer);
    progressTimer = null;
  }
}

function startProgressTimer() {
  stopProgressTimer();
  progressTimer = setInterval(() => {
    if (videoProgress.value >= 100) {
      isPlaying.value = false;
      videoProgress.value = 0;
      stopProgressTimer();
      return;
    }
    videoProgress.value += 1;
  }, 300);
}

function resetPlayback() {
  pausePreviewVideo();
  isPlaying.value = false;
  videoProgress.value = 0;
  stopProgressTimer();
}

function switchCate(cateId) {
  if (activeCateId.value === cateId) return;
  activeCateId.value = cateId;
  activeIdx.value = 0;
  resetPlayback();
}

function handleCardClick(index) {
  activeIdx.value = index;
  const item = displayItems.value[index];
  if (item?.isVideo) {
    claimPlayback();
    isPlaying.value = true;
    videoProgress.value = 0;
  } else {
    resetPlayback();
  }
}

function togglePlay() {
  if (!isPlaying.value) {
    claimPlayback();
  }
  isPlaying.value = !isPlaying.value;
}

function openItemDetail(item) {
  if (!item?.href) return;
  const href = router.resolve(item.href);
  window.open(href.href, '_blank');
}

watch(
  () => props.cates,
  (list) => {
    if (!list?.length) {
      activeCateId.value = null;
      return;
    }
    const exists = list.some((cate) => cate.id === activeCateId.value);
    if (!exists) {
      activeCateId.value = list[0].id;
      activeIdx.value = 0;
      resetPlayback();
    }
  },
  { immediate: true }
);

watch(
  displayItems,
  (list) => {
    if (!list.length) {
      activeIdx.value = 0;
      resetPlayback();
      return;
    }
    if (activeIdx.value >= list.length) {
      activeIdx.value = 0;
      resetPlayback();
    }
  }
);

watch(
  previewItem,
  (item) => {
    detectPortrait(item);
  },
  { immediate: true }
);

watch(isPlaying, (playing) => {
  if (playing && previewItem.value?.isVideo) {
    startProgressTimer();
    const video = previewVideoRef.value;
    if (video) {
      claimPlayback(video);
      video.muted = false;
      video.play().catch(() => {});
    }
  } else {
    pausePreviewVideo();
    stopProgressTimer();
  }
});

onBeforeUnmount(() => {
  stopProgressTimer();
});
</script>

<style scoped>
.ni-int__video--clickable {
  cursor: pointer;
}

.ni-int__empty {
  margin: 24px 0 0;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
}
</style>
